import { useCallback } from "react";
import { get, useFormContext } from "react-hook-form";

import { useRequestFormReviewValidation } from "@RequestFormV2/context/reviewValidation";
import { PAST_DATE_ERROR_TYPE, isDeadlineDateInPast } from "@RequestFormV2/validation/utils/isDeadlineDateInPast";
import { SERVER_ERROR_TYPE } from "@RequestFormV2/utils/applyApiFieldErrors";
import { hasLeafReviewError } from "@RequestFormV2/validation/utils/hasLeafReviewError";
import { isEmptyValue, isValidTimeValue } from "@RequestFormV2/validation/utils/validationMessages";

export { PAST_DATE_ERROR_TYPE };

export type ReviewFieldErrorClearOptions = {
  isTime?: boolean;
};

type ShouldClearReviewFieldErrorOptions = ReviewFieldErrorClearOptions & {
  /** False when re-checking an unchanged value (e.g. after errors update or on remount). */
  valueChanged?: boolean;
};

type ReviewFieldErrorLike = {
  type?: string;
  message?: string;
};

export function shouldClearReviewFieldError(
  fieldError: unknown,
  value: unknown,
  options?: ShouldClearReviewFieldErrorOptions,
): boolean {
  const errorType =
    fieldError && typeof fieldError === "object" && "type" in fieldError
      ? (fieldError as ReviewFieldErrorLike).type
      : undefined;

  // The API rejected a filled value, so keep the error until the value changes.
  if (errorType === SERVER_ERROR_TYPE) {
    return options?.valueChanged !== false;
  }

  if (options?.isTime && errorType === "hoursOrder") {
    return false;
  }

  if (errorType === PAST_DATE_ERROR_TYPE) {
    return !isEmptyValue(value) && !isDeadlineDateInPast(value);
  }

  return options?.isTime ? isValidTimeValue(value) : !isEmptyValue(value);
}

export function useReviewFieldErrorClear(fieldPath?: string, options?: ReviewFieldErrorClearOptions) {
  const { hasAttemptedReview, clearFieldError } = useRequestFormReviewValidation();
  const formContext = useFormContext();
  const errors = formContext?.formState?.errors ?? {};
  const getValues = formContext?.getValues;
  const isTime = options?.isTime;

  const tryClear = useCallback(
    (value?: unknown, valueChanged = true) => {
      if (!formContext || !fieldPath || !hasAttemptedReview || !clearFieldError) return;
      if (!hasLeafReviewError(errors, fieldPath)) return;

      const resolved = value ?? getValues?.(fieldPath);
      const fieldError = get(errors, fieldPath);

      if (shouldClearReviewFieldError(fieldError, resolved, { isTime, valueChanged })) {
        clearFieldError(fieldPath);
      }
    },
    [formContext, fieldPath, hasAttemptedReview, clearFieldError, errors, getValues, isTime],
  );

  return useCallback((value?: unknown, valueChanged?: boolean) => tryClear(value, valueChanged), [tryClear]);
}
